import { Icon } from '@iconify/react';
import { printConfigFor } from '../../Invoices/components/invoiceSettingDefaults';
import { formatIstDate } from '../../../../../utils/helpers/istDate';

const NoteDetailModal = ({ note, loading, onClose, onVoid, onPost, onPrint, onRecordPayment, voidLoading, postLoading, noteType }) => {
    if (!note) return null;

    const isCreditNote = noteType === 'CREDIT_NOTE';
    const noteLabel = isCreditNote ? 'Credit Note' : 'Debit Note';

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return formatIstDate(dateStr);
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'PAID': return 'tw-bg-emerald-100 tw-text-emerald-700';
            case 'POSTED': return 'tw-bg-blue-100 tw-text-blue-700';
            case 'DRAFT': return 'tw-bg-gray-100 tw-text-gray-700';
            case 'PARTIAL': return 'tw-bg-amber-100 tw-text-amber-700';
            case 'VOID': return 'tw-bg-red-100 tw-text-red-700';
            case 'CANCELLED': return 'tw-bg-gray-200 tw-text-gray-600';
            case 'UTILIZED': return 'tw-bg-purple-100 tw-text-purple-700';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    // Config snapshot
    const config = printConfigFor(note);

    // Map fields from API response
    const noteNo = note.document_number || '-';
    const member = note.member_details?.name || '-';
    const flat = note.flat || {};
    const unit = flat.flat_number || note.member_details?.flat_no || '-';
    const wing = flat.wing?.title || '-';

    // Entity details from ownership
    const ownership = flat.ownerships?.[0] || {};
    const entity = ownership.entity || {};
    const phone = entity.phone || '-';
    const email = entity.email || note.member_details?.email || '-';
    const memberGst = entity.gstin_no || note.member_details?.gst_no || '';
    const memberPan = entity.pan_no || '';

    const invoiceTypeName = note.invoiceType?.invoice_title || '-';
    const status = note.status || 'DRAFT';
    const issueDate = note.issue_date;
    const dueDate = note.due_date;
    const netPayable = parseFloat(note.net_payable || 0);
    const amountPaid = parseFloat(note.amount_paid || 0);
    const balance = parseFloat(note.balance_due || 0) || (netPayable - amountPaid);
    const taxAmount = parseFloat(note.tax_amount || 0);

    // Line items - separate charge items from system (tax) items
    const allItems = note.items || [];
    const SYSTEM_HEAD_CODES = ['CGST', 'SGST', 'IGST', 'PAR', 'DNR', 'INT', 'RND', 'ADJ'];
    const chargeItems = allItems.filter(item => !SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));
    const taxItems = allItems.filter(item => ['CGST', 'SGST', 'IGST'].includes(item.tariffHead?.head_code));
    const otherItems = allItems.filter(item => ['RND', 'ADJ'].includes(item.tariffHead?.head_code));

    const chargeSubtotal = chargeItems.reduce((sum, item) => sum + parseFloat(item.base_amount || item.system_amount || 0), 0);

    const reason = note.reason || '-';
    const remarks = note.remarks || '';
    const refInvoice = note.invoice;
    const society = note.society;

    const canPost = status === 'DRAFT';
    const canVoid = !['VOID', 'CANCELLED', 'UTILIZED'].includes(status);
    // Only a debit note is collected from the member. A draft is not billed yet, and a carried
    // forward note is collected on its successor, so neither can take a payment of its own.
    const canCollectPayment = !isCreditNote
        && !['DRAFT', 'PAID', 'VOID', 'CANCELLED', 'UTILIZED'].includes(status)
        && note.is_carried_forward !== 1;

    // Insights
    const getInsights = () => {
        const insights = [];
        if (status === 'VOID' || status === 'CANCELLED') {
            insights.push({ type: 'warning', icon: 'mdi:alert-circle', text: `This ${noteLabel.toLowerCase()} has been ${status.toLowerCase()}.` });
        }
        if (status === 'DRAFT') {
            insights.push({ type: 'tip', icon: 'mdi:lightbulb-outline', text: `This is a draft. Post it to make it official.` });
        }
        if (status === 'UTILIZED') {
            insights.push({ type: 'success', icon: 'mdi:check-decagram', text: `This ${noteLabel.toLowerCase()} has been fully utilized.` });
        }
        if (status === 'POSTED' && dueDate) {
            const dueDateObj = new Date(dueDate);
            const today = new Date();
            if (dueDateObj < today) {
                const daysOverdue = Math.floor((today - dueDateObj) / (1000 * 60 * 60 * 24));
                insights.push({ type: 'warning', icon: 'mdi:alert-circle', text: `${daysOverdue} days past due date.` });
            }
        }
        if (status === 'PAID') {
            insights.push({ type: 'success', icon: 'mdi:check-circle', text: `Fully settled.` });
        }
        if (refInvoice) {
            insights.push({ type: 'info', icon: 'mdi:link-variant', text: `Linked to invoice ${refInvoice.document_number || refInvoice.invoice_id}.` });
        }
        return insights;
    };

    const insights = getInsights();

    if (loading) {
        return (
            <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-p-8 tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4">Loading {noteLabel.toLowerCase()} details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
                {/* Modal Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${isCreditNote ? 'tw-bg-emerald-500' : 'tw-bg-orange-500'} tw-flex tw-items-center tw-justify-center`}>
                            <Icon icon={isCreditNote ? "mdi:note-plus-outline" : "mdi:note-minus-outline"} className="tw-text-white tw-text-xl" />
                        </div>
                        <div>
                            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-800">{noteNo}</h2>
                            <p className="tw-text-xs tw-text-gray-500">{noteLabel} - {invoiceTypeName}</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${getStatusColor(status)}`}>
                            {status}
                        </span>
                        <button onClick={onClose} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-400">
                            <Icon icon="mdi:close" className="tw-text-xl" />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="tw-flex-1 tw-overflow-y-auto tw-p-5">
                    <div className="tw-grid tw-grid-cols-3 tw-gap-5">
                        {/* Left Column */}
                        <div className="tw-col-span-2 tw-space-y-4">
                            {/* Member Info - Bill To */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:account-outline" className="tw-text-gray-400" /> {isCreditNote ? 'Credit To' : 'Debit To'}
                                </h3>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <p className="tw-font-medium tw-text-gray-800">{member}</p>
                                        <p className="tw-text-xs tw-text-gray-500 tw-mt-1">{wing}-{unit} {flat.flat_type ? `| ${flat.flat_type}` : ''}</p>
                                        {memberGst && (
                                            <p className="tw-text-xs tw-text-blue-600 tw-mt-1 tw-font-medium">GSTIN: {memberGst}</p>
                                        )}
                                        {memberPan && (
                                            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">PAN: {memberPan}</p>
                                        )}
                                    </div>
                                    <div className="tw-text-right">
                                        <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1">
                                            <Icon icon="mdi:phone-outline" /> {phone}
                                        </p>
                                        <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-1">
                                            <Icon icon="mdi:email-outline" /> {email}
                                        </p>
                                        {config.show_flat_area === 1 && (flat.carpet_area || note.member_details?.flat_area) && (
                                            <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-1">
                                                <Icon icon="mdi:ruler-square" /> {config.area_label || 'Area'}: {flat.carpet_area || note.member_details?.flat_area} sq.ft
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Reason & Remarks */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:text-box-outline" className="tw-text-gray-400" /> Reason
                                </h3>
                                <p className="tw-text-sm tw-text-gray-700">{reason}</p>
                                {remarks && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-2 tw-italic">Remarks: {remarks}</p>
                                )}
                            </div>

                            {/* Reference Invoice */}
                            {refInvoice && (
                                <div className="tw-bg-blue-50 tw-rounded-lg tw-p-4 tw-border tw-border-blue-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:file-document-outline" className="tw-text-blue-500" /> Reference Invoice
                                    </h3>
                                    <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">Invoice No</p>
                                            <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{refInvoice.document_number || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">Bill Date</p>
                                            <p className="tw-text-sm tw-text-gray-700">{formatDate(refInvoice.bill_date)}</p>
                                        </div>
                                        <div>
                                            <p className="tw-text-[11px] tw-text-gray-500">Amount</p>
                                            <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{formatCurrency(refInvoice.net_payable)}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Charge Items Table */}
                            <div className="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
                                <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:format-list-bulleted" className="tw-text-gray-400" /> {noteLabel} Items
                                    </h3>
                                </div>
                                <table className="tw-w-full tw-text-sm">
                                    <thead>
                                        <tr className="tw-border-b tw-border-gray-100">
                                            <th className="tw-text-left tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500">Description</th>
                                            <th className="tw-text-right tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500">Tax</th>
                                            <th className="tw-text-right tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {chargeItems.map((item, idx) => {
                                            const itemAmount = parseFloat(item.base_amount || item.system_amount || 0);
                                            return (
                                                <tr key={idx} className="tw-border-b tw-border-gray-50">
                                                    <td className="tw-px-4 tw-py-2 tw-text-gray-700">
                                                        {item.tariffHead?.head_name || item.description || '-'}
                                                        {item.description && item.tariffHead?.head_name && item.description !== item.tariffHead.head_name && (
                                                            <span className="tw-text-xs tw-text-gray-400 tw-ml-1">({item.description})</span>
                                                        )}
                                                        {item.taxRule && (
                                                            <p className="tw-text-[10px] tw-text-indigo-500 tw-mt-0.5">{item.taxRule.rule_name}</p>
                                                        )}
                                                    </td>
                                                    <td className="tw-px-4 tw-py-2 tw-text-right tw-text-gray-500">{formatCurrency(item.tax_amount)}</td>
                                                    <td className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-text-gray-800">{formatCurrency(itemAmount)}</td>
                                                </tr>
                                            );
                                        })}
                                        {chargeItems.length === 0 && (
                                            <tr>
                                                <td colSpan={3} className="tw-px-4 tw-py-4 tw-text-center tw-text-gray-400">No items found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        {/* Subtotal */}
                                        <tr className="tw-border-t tw-border-gray-100">
                                            <td colSpan={2} className="tw-px-4 tw-py-2 tw-text-right tw-text-gray-600 tw-text-xs">Subtotal</td>
                                            <td className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-text-gray-800">{formatCurrency(chargeSubtotal)}</td>
                                        </tr>

                                        {/* Tax items */}
                                        {taxItems.map((item, idx) => {
                                            const amount = parseFloat(item.base_amount || item.system_amount || 0);
                                            if (amount === 0) return null;
                                            return (
                                                <tr key={`tax-${idx}`} className="tw-border-t tw-border-gray-50">
                                                    <td colSpan={2} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">{item.tariffHead?.head_name || item.description}</td>
                                                    <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">{formatCurrency(amount)}</td>
                                                </tr>
                                            );
                                        })}

                                        {/* Fallback tax from invoice level */}
                                        {taxItems.length === 0 && taxAmount > 0 && (
                                            <tr className="tw-border-t tw-border-gray-50">
                                                <td colSpan={2} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">Tax</td>
                                                <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">{formatCurrency(taxAmount)}</td>
                                            </tr>
                                        )}

                                        {/* Other system items (Round Off, Adjustment) */}
                                        {otherItems.map((item, idx) => {
                                            const amount = parseFloat(item.base_amount || item.system_amount || 0);
                                            if (amount === 0) return null;
                                            return (
                                                <tr key={`other-${idx}`} className="tw-border-t tw-border-gray-50">
                                                    <td colSpan={2} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">{item.tariffHead?.head_name || item.description}</td>
                                                    <td className={`tw-px-4 tw-py-1.5 tw-text-right ${amount < 0 ? 'tw-text-red-600' : 'tw-text-gray-700'}`}>
                                                        {amount < 0 ? '- ' : ''}{formatCurrency(Math.abs(amount))}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {/* Net Payable */}
                                        <tr className="tw-bg-gray-50">
                                            <td colSpan={2} className="tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-800 tw-text-right">Net Payable</td>
                                            <td className="tw-px-4 tw-py-2 tw-text-right tw-font-bold tw-text-gray-800">{formatCurrency(netPayable)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* Right Column - Summary & Actions */}
                        <div className="tw-space-y-4">
                            {/* Amount Summary */}
                            <div className={`tw-bg-gradient-to-br ${isCreditNote ? 'tw-from-emerald-500 tw-to-emerald-600' : 'tw-from-orange-500 tw-to-orange-600'} tw-rounded-lg tw-p-4 tw-text-white`}>
                                <p className="tw-text-white/70 tw-text-xs tw-mb-1">Net Payable</p>
                                <p className="tw-text-2xl tw-font-bold">{formatCurrency(netPayable)}</p>
                                <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-white/20 tw-space-y-2">
                                    <div className="tw-flex tw-justify-between tw-text-sm">
                                        <span className="tw-text-white/70">Paid</span>
                                        <span className="tw-font-medium">{formatCurrency(amountPaid)}</span>
                                    </div>
                                    <div className="tw-flex tw-justify-between tw-text-sm">
                                        <span className="tw-text-white/70">Balance</span>
                                        <span className="tw-font-medium">{formatCurrency(balance)}</span>
                                    </div>
                                </div>
                                {amountPaid > 0 && (
                                    <div className="tw-mt-3">
                                        <div className="tw-flex tw-justify-between tw-text-xs tw-mb-1">
                                            <span className="tw-text-white/70">Progress</span>
                                            <span>{Math.round((amountPaid / netPayable) * 100)}%</span>
                                        </div>
                                        <div className="tw-w-full tw-bg-white/20 tw-rounded-full tw-h-2">
                                            <div className="tw-bg-white tw-h-2 tw-rounded-full tw-transition-all" style={{ width: `${Math.min((amountPaid / netPayable) * 100, 100)}%` }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Dates */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-3">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:calendar-plus-outline" /> Issue Date
                                    </span>
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-800">{formatDate(issueDate)}</span>
                                </div>
                                {dueDate && (
                                    <div className="tw-flex tw-items-center tw-justify-between">
                                        <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                            <Icon icon="mdi:calendar-alert-outline" /> Due Date
                                        </span>
                                        <span className="tw-text-sm tw-font-medium tw-text-gray-800">{formatDate(dueDate)}</span>
                                    </div>
                                )}
                                {society && (
                                    <div className="tw-flex tw-items-center tw-justify-between">
                                        <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                            <Icon icon="mdi:office-building-outline" /> Society
                                        </span>
                                        <span className="tw-text-xs tw-font-medium tw-text-gray-800">{society.name}</span>
                                    </div>
                                )}
                            </div>

                            {/* Insights */}
                            {insights.length > 0 && (
                                <div className="tw-bg-gradient-to-br tw-from-purple-50 tw-to-indigo-50 tw-rounded-lg tw-p-4 tw-border tw-border-purple-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-purple-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:robot-outline" className="tw-text-purple-500" /> Insights
                                    </h3>
                                    <div className="tw-space-y-2">
                                        {insights.map((insight, idx) => (
                                            <div key={idx} className={`tw-flex tw-items-start tw-gap-2 tw-text-xs tw-p-2 tw-rounded-md ${
                                                insight.type === 'warning' ? 'tw-bg-amber-50 tw-text-amber-800' :
                                                insight.type === 'success' ? 'tw-bg-emerald-50 tw-text-emerald-800' :
                                                insight.type === 'tip' ? 'tw-bg-blue-50 tw-text-blue-800' :
                                                'tw-bg-gray-50 tw-text-gray-700'
                                            }`}>
                                                <Icon icon={insight.icon} className="tw-text-base tw-flex-shrink-0 tw-mt-0.5" />
                                                <span>{insight.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quick Actions */}
                            <div className="tw-space-y-2">
                                {canPost && (
                                    <button
                                        onClick={onPost}
                                        disabled={postLoading}
                                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-emerald-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-emerald-700 tw-transition-colors disabled:tw-opacity-50"
                                    >
                                        {postLoading ? (
                                            <><div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-white"></div> Posting...</>
                                        ) : (
                                            <><Icon icon="mdi:check-circle" /> Post {noteLabel}</>
                                        )}
                                    </button>
                                )}
                                {canCollectPayment && onRecordPayment && (
                                    <button
                                        onClick={onRecordPayment}
                                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors"
                                    >
                                        <Icon icon="mdi:cash-plus" /> Record Payment
                                    </button>
                                )}
                                {onPrint && (
                                    <button
                                        onClick={onPrint}
                                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-gray-200 tw-text-gray-700 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-50 tw-transition-colors"
                                    >
                                        <Icon icon="mdi:printer" /> Print {noteLabel}
                                    </button>
                                )}
                                {canVoid && status !== 'DRAFT' && (
                                    <button
                                        onClick={onVoid}
                                        disabled={voidLoading}
                                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-red-200 tw-text-red-600 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-red-50 tw-transition-colors disabled:tw-opacity-50"
                                    >
                                        {voidLoading ? (
                                            <><div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-red-600"></div> Voiding...</>
                                        ) : (
                                            <><Icon icon="mdi:close-circle" /> Void {noteLabel}</>
                                        )}
                                    </button>
                                )}
                                <button onClick={onClose} className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-gray-200 tw-text-gray-700 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-50 tw-transition-colors">
                                    <Icon icon="mdi:close" /> Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoteDetailModal;
